import { checkClaimsWeb, spLogTags } from '@eventr/common'
import {
    ColoursObj,
    drawerStore,
    Icon,
    MenuButton,
    MobileConfirmDialog,
    TDialogStore,
    Typography,
    useDialogStore,
} from '@eventr/common-storybook'
import { Stack } from '@mui/material'
import { useLocation } from 'react-router-dom'
import { useShallow } from 'zustand/react/shallow'
import { TeamMember } from '../../../home/overlay/TeamMember'
import { MemberProfilePic } from '../../../home/team/MemberProfilePic'
import i18n from '../../../i18n/i18next'
import { useBoundStore } from '../../../state/bound.store'
import { defaultAddSnack } from '../../../state/overlayState/overlayContext'
import { loadingSignal } from '../../../state/overlayState/overlaySignals'
import { useScrollContext } from '../../../state/scrollState/scrollContext'
import { scrollRef } from '../../../state/scrollState/scrollSignals'
import { NavigationView } from '../../layout/NavigationView'
import { PopoverItemProps } from '../../layout/TopToolbar'
import { useGetChatName } from '../hooks/useGetChatName'
import { deleteGroupChannel, useRocketChatContext } from '../rocketChatProvider'
import { useChatBodyContext } from './chatProvider'

const groupTypeIcons = [
    { type: 'Group', icon: 'Users' },
    { type: 'User', icon: 'User' },
    { type: 'Accommodation', icon: 'Stays' },
    { type: 'Flight', icon: 'Flight' },
    { type: 'Activity', icon: 'Activity' },
    { type: 'Transport', icon: 'Ferry' },
    { type: 'ParkingBooking', icon: 'Parking' },
    { type: 'HireCar', icon: 'Hire_Car' },
    { type: 'VehicleAllocation', icon: 'Car_Allocation' },
    { type: 'Activity', icon: 'Activity' },
]

export const ChatInfoMenu = () => {
    const { api, directChannelUser, isGroup } = useChatBodyContext()
    const { chatView, setRooms } = useRocketChatContext()
    const setOverride = useBoundStore((s) => s.setOverride)
    const { setScrollBehaviour } = useScrollContext()

    const { replaceDialog, clearDialog }: TDialogStore = useDialogStore(
        useShallow((s: TDialogStore) => ({
            clearDialog: s.clearDialog,
            replaceDialog: s.replaceDialog,
        }))
    )
    const currentUrlSplit = useLocation()?.pathname?.split('/') ?? []
    const roomID = currentUrlSplit[currentUrlSplit.length - 1]
    const roomName = useGetChatName(roomID)

    const renderGroupIcon = (groupType: string) => {
        const matchedIcon = groupTypeIcons.find(
            (item) => item.type === groupType
        )
        return (
            <Icon
                width="56px"
                height="56px"
                iconColour={ColoursObj.core.primary}
                bubble
                bubbleColour={ColoursObj.shades.shade4}
                name={(matchedIcon?.icon as any) ?? 'Activity'}
            />
        )
    }

    function scrollDelayed() {
        setScrollBehaviour('initial')
        setTimeout(() => {
            scrollRef.value.current.scrollTop =
                scrollRef.value.current.scrollHeight -
                scrollRef.value.current.clientHeight
        }, 150)
    }
    const onDelete = async () => {
        try {
            loadingSignal.value = true
            await deleteGroupChannel(roomID ?? '')
                .then(async (c) => {
                    if (c.status === 'success') {
                        defaultAddSnack(
                            i18n.t('snack.rocket.deleteGroupSuccess'),
                            'success'
                        )
                        setRooms(roomID)
                        window.pushPath('tabs/rocketChat')
                    } else if (c.status === 'error') {
                        const identifierObj = JSON.parse(c.data)

                        const error = deleteErrorMessage(
                            identifierObj.error_type
                        )

                        defaultAddSnack(error, 'error')
                        spLogTags('Error deleting channel', [
                            'rocketchat',
                            'ERROR',
                        ])
                        spLogTags(c, ['rocketchat', 'ERROR'])
                    } else {
                        spLogTags(c, ['rocketchat', 'ERROR'])
                    }
                })
                .finally(() => {
                    loadingSignal.value = false
                })
        } catch (error) {
            loadingSignal.value = false
            defaultAddSnack(i18n.t('snack.rocket.deleteGroupError'), 'error')
            spLogTags('Exception deleting channel', ['rocketchat', 'ERROR'])
            spLogTags(error, ['rocketchat', 'ERROR'])
        }
    }

    const groupOptions: PopoverItemProps[] = [
        {
            title: i18n.t('rocketChat.editGroup'),
            action: () => {
                chatView.value = 'manageGroupChannel'
            },
        },
    ]

    //Only delete group chats
    if (checkClaimsWeb([`/`], ['deleteGroupChat'])) {
        groupOptions.push({
            title: i18n.t('rocketChat.deleteGroup'),
            color: ColoursObj.states.error,
            action: () =>
                replaceDialog({
                    node: (
                        <MobileConfirmDialog
                            header={i18n.t('rocketChat.deleteGroup')}
                            text={i18n.t('rocketChat.deleteGroupMessage')}
                            onCancel={() => {
                                onDelete()
                                clearDialog()
                            }}
                            onConfirm={() => {
                                clearDialog()
                            }}
                            cancelText={i18n.t('info-dialog.yes')}
                            confirmText={i18n.t('info-dialog.no')}
                        />
                    ),
                    backdropClosable: false,
                }),
        })
    }

    return (
        <NavigationView
            navigationBarTitle={
                <Typography variant="mobileH3">
                    {api.value.roomType === 'c'
                        ? i18n.t('rocketChat.groupMenu')
                        : i18n.t('rocketChat.chatMenu')}
                </Typography>
            }
            navigationBarTrailing={
                isGroup.value
                    ? [
                          {
                              id: 'trailingItemAddButton',
                              icon: 'More',
                              popoverItems: groupOptions,
                          },
                      ]
                    : []
            }
            backButtonAction={() => {
                scrollDelayed()
                chatView.value = 'default'
                scrollDelayed()
            }}
            usePageTransition
        >
            <Stack
                width={'100%'}
                direction={'row'}
                justifyContent={'space-between'}
                padding={'16px'}
                boxSizing={'border-box'}
                onClick={() => {
                    if (api.value.roomType === 'd') {
                        // direct chats
                        setOverride(
                            directChannelUser?.customFields?.wpUserID ?? null,
                            false
                        )
                        drawerStore().openDrawer({ node: <TeamMember /> })
                    } else if (api.value.roomType === 'c') {
                        // group chats
                    } else {
                        spLogTags('Error with chat room type', [
                            'error',
                            'rocketchat',
                        ])
                    }
                }}
                style={{ borderBottom: '1px solid #F5F9FA' }}
            >
                <Stack
                    width={'100%'}
                    direction={'row'}
                    alignItems={'center'}
                    spacing={'8px'}
                >
                    {api.value.roomType === 'd' && (
                        <MemberProfilePic
                            rcUser={directChannelUser}
                            sideLength={56}
                        />
                    )}
                    {api.value.roomType === 'c' &&
                        renderGroupIcon(api.value.groupType)}
                    <Stack gap={'4px'}>
                        <Typography
                            variant="bodyBold"
                            overflow={'hidden'}
                            textOverflow={'ellipsis'}
                        >
                            {roomName ?? ''}
                        </Typography>
                        <Typography variant="bodyRegular">
                            {api.value.roomType === 'c'
                                ? api.value.groupType
                                : 'Team member'}
                        </Typography>
                    </Stack>
                </Stack>
                <Stack
                    display="flex"
                    justifyContent={'center'}
                    paddingLeft={'16px'}
                >
                    {api.value.roomType === 'd' && (
                        <Icon name="Chevron_Right" />
                    )}
                </Stack>
            </Stack>
            <Stack padding={'16px'} gap={'8px'}>
                <MenuButton
                    endIcon="Chevron_Right"
                    onClick={() => {
                        chatView.value = 'chatMembers'
                    }}
                    endIconColour={ColoursObj.font.primary}
                    style={{
                        background: ColoursObj.backgrounds.secondary,
                    }}
                >
                    {' '}
                    <Typography
                        variant="bodyRegular"
                        color={ColoursObj.font.primary}
                    >
                        {api.value.roomType === 'd'
                            ? i18n.t('rocketChat.chatMembers')
                            : i18n.t('rocketChat.groupMembers')}
                    </Typography>
                </MenuButton>
            </Stack>
        </NavigationView>
    )
}

function deleteErrorMessage(msg: string) {
    switch (msg) {
        case 'error-room-not-found':
            return i18n.t('snack.rocket.roomNotFound')
        case 'error-not-allowed':
            return i18n.t('snack.rocket.userNotAllowedDelete')
        case 'error-invalid-room':
            return i18n.t('snack.rocket.invalidRoomId')
        case 'error-unauthorized':
            return i18n.t('snack.rocket.unauthorisedDelete')
        case 'error-cannot-delete-room':
            return i18n.t('snack.rocket.cannotDeleteRoom')
        default:
            return i18n.t('snack.rocket.deleteGroupError')
    }
}
